import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';

export interface FormData {
    subjectId: string;
    programId: string;
    isTrial: boolean;
    isVisible: boolean;
    title: string;
    description: string;
    thumbnail: string;
    videoUrl: string;
    videoUrl1: string;
    videoUrl2: string;
    duration: string;
}

type UseTermFormProps = {
    initialFormData: FormData;
    debounceDelay?: number;
};

export function useProgramForm({ initialFormData, debounceDelay = 300 }: UseTermFormProps) {
    // Form data state from the api call
    const [formData, setFormData] = useState<FormData>(initialFormData);

    // Raw form debouncing and data or the dirty form data once the user
    // filled out information in textfields
    const [rawFormData, setRawFormData] = useState<FormData>(initialFormData);
    const [debouncedFormData] = useDebounce(rawFormData, debounceDelay);

    // Touch fields recording to handle error textfield validation
    const [touchedFields, setTouchedFields] = useState<Record<keyof FormData, boolean>>({
        subjectId: false,
        programId: false,
        isTrial: false,
        isVisible: false,
        title: false,
        description: false,
        thumbnail: false,
        videoUrl: false,
        videoUrl1: false,
        videoUrl2: false,
        duration: false
    });

    // Debouncing mechanism for form state (create / edit)
    useEffect(() => {
        setFormData(debouncedFormData);
    }, [debouncedFormData]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value, type, checked } = e.target as HTMLInputElement;
            setRawFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [],
    );

    const handleBlur = useCallback(
        (field: keyof FormData) => {
            setTouchedFields((prev) => ({ ...prev, [field]: true }));
        },
        [],
    );

    const resetForm = useCallback((newInitialData?: FormData) => {
        const resetData = newInitialData || initialFormData;
        setRawFormData(resetData);
        setFormData(resetData);
        setTouchedFields({
            subjectId: false,
            programId: false,
            isTrial: false,
            isVisible: false,
            title: false,
            description: false,
            thumbnail: false,
            videoUrl: false,
            videoUrl1: false,
            videoUrl2: false,
            duration: false
        });
    }, [initialFormData]);

    const isFormValid = useCallback(() => {
        return (
            !!formData.subjectId?.trim() &&
            !!formData.programId?.trim() &&
            formData.isTrial !== undefined &&
            formData.isVisible !== undefined &&
            !!formData.title?.trim() &&
            !!formData.description?.trim() &&
            !!formData.thumbnail?.trim() &&
            !!formData.videoUrl?.trim() &&
            !!formData.duration?.trim()
        );
    }, [formData]);

    return {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid,
    };
}
